import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { send } from '../../api/client';
import { useAction, useList } from '../../api/hooks';
import type { NotificationRow } from '../../api/types';
import { relativeTime } from '../../lib/format';
import { Btn, Empty, Icon, IconBadge, ListPlain, Loading } from '../ui';

export const SEVERITY_ICON = { info: 'info', good: 'check', warn: 'warn', bad: 'warn' } as const;

export function NotificationList({ rows, onOpen }: { rows: NotificationRow[]; onOpen: (n: NotificationRow) => void }) {
  if (!rows.length) return <Empty>Nothing new.</Empty>;
  return (
    <ListPlain>
      {rows.map((n) => (
        <li key={n.id} style={{ opacity: n.read ? 0.6 : 1 }}>
          <IconBadge name={SEVERITY_ICON[n.severity]} tone={n.severity} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <b style={{ fontWeight: 600, fontSize: '.87rem' }}>{n.title}</b>
            {n.body ? <span className="sub">{n.body}</span> : null}
            <span className="sub">
              {relativeTime(n.createdAt)}
              {n.link ? (
                <>
                  {' · '}
                  <a href={n.link} onClick={(e) => { e.preventDefault(); onOpen(n); }}>Open</a>
                </>
              ) : null}
            </span>
          </div>
        </li>
      ))}
    </ListPlain>
  );
}

/** Slide-over with the latest notifications (reference `openDrawer`). */
export function NotificationsDrawer({ onClose }: { onClose: () => void }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const list = useList<NotificationRow>('/notifications', { limit: 30 });
  const refresh = () => void qc.invalidateQueries({ predicate: (q) => typeof q.queryKey[0] === 'string' && (q.queryKey[0] as string).startsWith('/notifications') });
  const readAll = useAction(() => send('post', '/notifications/read-all'), { success: 'All notifications marked as read', onSuccess: refresh });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const open = async (n: NotificationRow) => {
    if (!n.read) await send('patch', `/notifications/${n.id}/read`).catch(() => undefined);
    refresh();
    onClose();
    if (n.link) nav(n.link);
  };

  return createPortal(
    <>
      <div className="scrim" style={{ background: 'rgba(8,16,12,.25)' }} onMouseDown={onClose} />
      <aside className="drawer" aria-label="Notifications">
        <div className="panel-head">
          <h2>Notifications</h2>
          <div className="row">
            <Btn size="sm" onClick={() => readAll.mutate()}>Mark all read</Btn>
            <Btn size="sm" tone="ghost" aria-label="Close" onClick={onClose}>
              <Icon name="x" />
            </Btn>
          </div>
        </div>
        <div style={{ overflowY: 'auto' }}>{list.isLoading ? <Loading /> : <NotificationList rows={list.data?.data ?? []} onOpen={open} />}</div>
      </aside>
    </>,
    document.body,
  );
}
